import { useHtmlGenerationLoader } from "@/composables/ui/useHtmlGenerationLoader";
import { useSupportStore } from "@/stores/supports/useSupportStore";
import type { Ref } from "@vue/runtime-core";
import { useRoute, useRouter } from "vue-router";
import { useToast } from "vue-toastification";

export function useHtmlDownload(supportCanvasRef: Ref<any>) {
  const route = useRoute();
  const router = useRouter();
  const toast = useToast();
  const supportStore = useSupportStore();
  const { hideLoader } = useHtmlGenerationLoader();

  const checkForHtmlDownload = async () => {
    console.log("🔍 useHtmlDownload.checkForHtmlDownload() - VERIFICATION", {
      hasDownloadQuery: route.query.downloadHTML === "true",
      query: route.query,
      path: route.path,
      timestamp: new Date().toISOString(),
    });

    if (route.query.downloadHTML === "true") {
      console.log(
        "🔄 Paramètre downloadHTML=true détecté - Démarrage du processus..."
      );

      // Attendre que le support soit chargé
      const maxWait = 5000; // 5 secondes max
      const startTime = Date.now();

      console.log("⏳ Attente du chargement du support...", { maxWait });

      const waitForSupport = () => {
        const elapsedTime = Date.now() - startTime;
        console.log(`🕰️ Attente en cours... (${elapsedTime}ms/${maxWait}ms)`, {
          hasSupport: !!supportStore.currentSupport,
          supportId: supportStore.currentSupport?.id,
          hasCanvas: !!supportCanvasRef.value,
          canvasMethod: supportCanvasRef.value?.generateAndDownloadHTML
            ? "disponible"
            : "indisponible",
        });

        if (supportStore.currentSupport && supportCanvasRef.value) {
          console.log(
            "✅ Support et canvas chargés - Démarrage du téléchargement..."
          );
          // Déclencher le téléchargement HTML après un petit délai
          setTimeout(async () => {
            try {
              console.log("📩 Appel generateAndDownloadHTML()...");
              const downloadStartTime = performance.now();

              await supportCanvasRef.value.generateAndDownloadHTML();

              const downloadEndTime = performance.now();
              console.log("✅ Téléchargement HTML terminé avec succès!", {
                duration: `${(downloadEndTime - downloadStartTime).toFixed(
                  2
                )}ms`,
              });

              // Cacher le loader et retourner à la liste des documents
              console.log("🔙 Retour à la liste des documents...");
              hideLoader();
              router.push("/documents");
            } catch (error) {
              console.error("❌ ERREUR lors du téléchargement HTML:", {
                error,
                message: error instanceof Error ? error.message : String(error),
                stack:
                  error instanceof Error ? error.stack : "Pas de stack trace",
                timestamp: new Date().toISOString(),
              });
              toast.error("Erreur lors de la génération du HTML");
              hideLoader();
              router.push("/documents");
            }
          }, 1000);
        } else if (elapsedTime < maxWait) {
          console.log("⏳ Nouvel essai dans 100ms...");
          // Réessayer après 100ms si le support n'est pas encore chargé
          setTimeout(waitForSupport, 100);
        } else {
          console.error(
            "❌ TIMEOUT: Support non chargé pour le téléchargement HTML",
            {
              elapsedTime,
              maxWait,
              hasSupport: !!supportStore.currentSupport,
              hasCanvas: !!supportCanvasRef.value,
            }
          );
          toast.error("Erreur: Impossible de charger le support");
          hideLoader();
          router.push("/documents");
        }
      };

      waitForSupport();
    } else {
      console.log("🚫 Pas de paramètre downloadHTML - Pas d'action");
    }
  };

  return {
    checkForHtmlDownload,
  };
}
